import React, { memo, useEffect, useMemo, useState } from '../../lib/teact/teact';
import { getActions } from '../../global';

import type { ApiWalletPrepaidOverview } from '../../api/types';
import type { Account } from '../../global/types';

import { dropEnclaveSessionHold, holdEnclaveSession } from '../../global/helpers/enclave';
import { parseAccountId } from '../../util/account';
import { logDebugError } from '../../util/logs';
import { shortenAddress } from '../../util/shortenAddress';
import { callApiWithThrow } from '../../api';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import PasswordForm from '../ui/PasswordForm';
import Spinner from '../ui/Spinner';

import styles from './PrepaidBalanceLinkModal.module.scss';

interface OwnProps {
  isOpen: boolean;
  accountId?: string;
  accounts?: Record<string, Account>;
  overview?: ApiWalletPrepaidOverview;
  enclaveToken?: string;
  isEnclaveSessionValid: boolean;
  onClose: NoneToVoidFunction;
  onLinked: (overview: ApiWalletPrepaidOverview) => void;
}

const DISCOVERY_CONCURRENCY = 3;

export function buildBalanceChoices(
  candidates: Array<[string, Account]>,
  candidateOverviews: Record<string, ApiWalletPrepaidOverview>,
  fallbackName: string,
) {
  const seen = new Set<string>();
  const overviewsByAddress = new Map<string, ApiWalletPrepaidOverview>();
  Object.values(candidateOverviews).forEach((overview) => {
    overview.addresses.forEach(({ address }) => overviewsByAddress.set(address, overview));
  });
  return candidates.flatMap(([id, account]) => {
    const address = account.byChain.tron!.address;
    const candidateOverview = candidateOverviews[id] || overviewsByAddress.get(address);
    const groupKey = candidateOverview?.balance_id || address;
    if (seen.has(groupKey)) return [];
    seen.add(groupKey);
    return [{
      id,
      address,
      name: candidateOverview?.name || account.title || fallbackName,
      walletName: account.title || fallbackName,
      walletsCount: candidateOverview?.addresses.length || 1,
    }];
  });
}

export async function discoverBalanceOverviews(
  candidates: Array<[string, Account]>,
  loadOverview: (id: string) => Promise<ApiWalletPrepaidOverview | undefined>,
  isCancelled: () => boolean,
) {
  const overviews: Record<string, ApiWalletPrepaidOverview> = {};
  const knownGroupsByAddress = new Map<string, ApiWalletPrepaidOverview>();
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < candidates.length && !isCancelled()) {
      const [id, account] = candidates[nextIndex++];
      const knownGroup = knownGroupsByAddress.get(account.byChain.tron!.address);
      if (knownGroup) {
        overviews[id] = knownGroup;
        continue;
      }

      try {
        const candidateOverview = await loadOverview(id);
        if (isCancelled()) return;
        if (candidateOverview) {
          overviews[id] = candidateOverview;
          candidateOverview.addresses.forEach((item) => knownGroupsByAddress.set(item.address, candidateOverview));
        }
      } catch (requestError) {
        if (!isCancelled()) logDebugError('Fee balance candidate', requestError);
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(DISCOVERY_CONCURRENCY, candidates.length) }, () => worker()));
  return overviews;
}

function PrepaidBalanceLinkModal({
  isOpen, accountId, accounts, overview, enclaveToken, isEnclaveSessionValid, onClose, onLinked,
}: OwnProps) {
  const { releaseEnclaveSession } = getActions();
  const lang = useLang();
  const [candidateId, setCandidateId] = useState('');
  const [search, setSearch] = useState('');
  const [candidateOverviews, setCandidateOverviews] = useState<Record<string, ApiWalletPrepaidOverview>>({});
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState('');

  const linkedAddressKey = overview?.addresses.map(({ address }) => address).join('|') || '';
  const candidates = useMemo(() => {
    const linkedAddresses = new Set(linkedAddressKey.split('|'));
    const network = accountId ? parseAccountId(accountId).network : undefined;
    return Object.entries(accounts ?? {}).filter(([id, account]) => {
      const address = account.byChain.tron?.address;
      return id !== accountId && parseAccountId(id).network === network
        && Boolean(address) && !linkedAddresses.has(address)
        && account.type !== 'view' && account.type !== 'hardware';
    });
  }, [accountId, accounts, linkedAddressKey]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const token = isEnclaveSessionValid ? enclaveToken : undefined;
    if (token) holdEnclaveSession(token);
    setCandidateOverviews({});
    setIsDiscovering(true);

    void (async () => {
      try {
        const next = await discoverBalanceOverviews(candidates, async (id) => {
          let candidateOverview = await callApiWithThrow('fetchWalletPrepaidOverview', id);
          if (!candidateOverview && token) {
            candidateOverview = await callApiWithThrow('authorizeWalletPrepaid', id, token);
          }
          return candidateOverview;
        }, () => cancelled);
        if (!cancelled) setCandidateOverviews(next);
      } finally {
        if (!cancelled) {
          setIsDiscovering(false);
        }
        if (token && dropEnclaveSessionHold(token)) releaseEnclaveSession({ enclaveToken: token });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, candidates, enclaveToken, isEnclaveSessionValid]);

  const choices = useMemo(() => buildBalanceChoices(candidates, candidateOverviews, lang('Wallet')),
    [candidates, candidateOverviews, lang]);
  const visibleChoices = choices.filter(({ name, walletName, address }) => (
    `${name} ${walletName} ${address}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  ));

  const close = useLastCallback(() => {
    setCandidateId('');
    setSearch('');
    setError('');
    onClose();
  });

  const authorizeLink = useLastCallback(async (token: string) => {
    holdEnclaveSession(token);
    if (!accountId || !candidateId || isLinking) {
      if (dropEnclaveSessionHold(token)) releaseEnclaveSession({ enclaveToken: token });
      return;
    }
    setIsLinking(true);
    setError('');
    try {
      const result = await callApiWithThrow('linkWalletPrepaidAccounts', accountId, candidateId, token);
      if (!result || 'error' in result) throw new Error(result?.error || 'WalletPrepaidLinkFailed');
      onLinked(result);
      close();
    } catch (linkError) {
      logDebugError('Fee balance link', linkError);
      setError(lang('$prepaid_unavailable'));
      setCandidateId('');
    } finally {
      setIsLinking(false);
      if (dropEnclaveSessionHold(token)) releaseEnclaveSession({ enclaveToken: token });
    }
  });

  return (
    <Modal isOpen={isOpen} title={lang('Choose Balance')} hasCloseButton onClose={close}>
      {candidateId ? (
        <PasswordForm
          isActive={isOpen}
          isLoading={isLinking}
          operationType="passcode"
          error={error}
          extraAuthUsages={1}
          submitLabel={lang('Connect to Balance')}
          noAutoConfirm
          onAuthorize={authorizeLink}
          onCancel={() => setCandidateId('')}
          onUpdate={() => setError('')}
        />
      ) : (
        <div className={styles.content}>
          {error && <p className={styles.error}>{error}</p>}
          <Input label={lang('Search')} value={search} onInput={setSearch} />
          {isDiscovering ? (
            <div className={styles.loading} role="status" aria-label={lang('Loading...')}><Spinner /></div>
          ) : (
            <div className={styles.list}>
              {visibleChoices.map(({ id, name, walletName, address, walletsCount }) => (
                <Button isSimple className={styles.choice} onClick={() => setCandidateId(id)} key={id}>
                  <span className={styles.choiceContent}>
                    <strong>{name}</strong>
                    <small>
                      {walletName !== name && `${walletName} · `}
                      {walletsCount > 1 ? lang('$wallets_amount', walletsCount) : shortenAddress(address, 7)}
                    </small>
                  </span>
                  <i className="icon-chevron-right" aria-hidden />
                </Button>
              ))}
              {!visibleChoices.length && <p className={styles.empty}>{lang('No Results')}</p>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default memo(PrepaidBalanceLinkModal);
